<template>
  <div class="user-resource-manage-ui">
    <EditableList
      :list="users"
      :table-headers="tableHeaders"
      :is-loading="isLoading"
      :has-error="hasError"
      :pagination="pagination"
      :is-edit-disabled="(user: TUser) => !canManageUser(user)"
      :is-delete-disabled="(user: TUser) => !canDeleteUser(user)"
      :variation-class-name="variationClassName"
      :list-item-description="listItemDescription"
      @fetch="fetchUsers"
      @edit="onEdit"
      @delete="onDelete"
    >
      <template #header>
        <div class="user-resource-manage-ui__header">
          <div class="user-resource-manage-ui__header-controls">
            <div class="user-resource-manage-ui__header-controls-search">
              <FilterInput v-model="searchQuery" label="Search" variant-class-name="slim" />
            </div>
            <div
              v-if="$slots['extra-controls']"
              class="user-resource-manage-ui__header-controls-extra"
            >
              <slot name="extra-controls" />
            </div>
          </div>
          <div class="user-resource-manage-ui__form">
            <UserResourceForm
              :model="model"
              :patch-data-client="patchDataClient"
              :full-control="fullControl"
              form-feedback-variation-class-name="no-margin"
              @upsert="onAdd"
            />
          </div>
        </div>
      </template>
      <template v-if="$slots['table-header']" #table-header>
        <slot name="table-header" />
      </template>
      <template #row="{ item }">
        <slot name="row" :item="item">
          <EditableListText>
            <p class="user-resource-manage-ui__email" :title="item.email">{{ item.email }}</p>
            <UserResourceManagePassword :user-id="item.id" :user-email="item.email" />
          </EditableListText>
          <EditableListText>{{ item.name }}</EditableListText>
          <EditableListText multi-line>{{ patchesString(item.patches) }}</EditableListText>
          <EditableListText>{{ item.role }}</EditableListText>
        </slot>
      </template>
    </EditableList>
  </div>
</template>

<script setup lang="ts">
  // Types
  import type {
    IUserResourceModel,
    TPaginatedUsers,
    TUser,
    TUserIndexPayload,
    TUserPatch,
  } from '~/misc/user-resource/types';
  import type { TPagination } from '~/misc/resource/types';
  import type { TUser as TAuthUser } from '~/misc/auth/types';
  import type { TPatch, TPatchDataClient } from '~/misc/patch/types';
  import { EUserRole } from '~/misc/user/types';

  // Services
  import { PaginationCalculator } from '~/misc/pagination/services/pagination-calculator';
  import { PatchLabelResolver } from '~/misc/patch/services/label-resolver';

  // Composables
  import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';

  // Components
  import UserResourceForm from '~/misc/user-resource/manage/form/UserResourceForm.vue';
  import EditableList from '~/misc/editable-list/EditableList.vue';
  import EditableListText from '~/misc/editable-list/text/EditableListText.vue';
  import UserResourceManagePassword from '~/misc/user-resource/manage/password/UserResourcePassword.vue';
  import FilterInput from '~/misc/filter-input/FilterInput.vue';

  const UpdateModal = defineAsyncComponent(
    () => import('~/misc/user-resource/manage/update-modal/UserResourceUpdateModal.vue')
  );

  const props = defineProps({
    model: {
      type: Object as PropType<IUserResourceModel>,
      required: true,
    },
    patchDataClient: {
      type: Object as PropType<TPatchDataClient>,
      required: true,
    },
    authUser: {
      type: Object as PropType<TAuthUser | null>,
      default: null,
    },
    fullControl: {
      type: Boolean,
      default: false,
    },
    variationClassName: {
      type: String,
      default: 'user-resource',
    },
    tableHeaders: {
      type: Array as PropType<string[]>,
      default: () => ['Email', 'Name', 'Areas', 'Role', ''],
    },
    listItemDescription: {
      type: String,
      default: '',
    },
    refetchTrigger: {
      type: Number,
      default: 0,
    },
    payload: {
      type: Object as PropType<TUserIndexPayload>,
      default: () => ({}),
    },
  });

  const { show: showModal } = useModal();
  const { notifySuccess, notifyFailure } = useNotifyResource();

  const users: Ref<TUser[]> = ref<TUser[]>([]);
  const pagination: Ref<TPagination | null> = ref<TPagination | null>(null);
  const isLoading: Ref<boolean> = ref<boolean>(false);
  const hasError: Ref<boolean> = ref<boolean>(false);
  const searchQuery: Ref<string> = ref<string>('');

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const setHasError = (state: boolean) => {
    hasError.value = state;
  };

  const setUsers = (data: TUser[]) => {
    users.value = data;
  };

  const setPagination = (data: TPagination | null) => {
    pagination.value = data;
  };

  const fetchUsers = async (page?: number) => {
    setIsLoading(true);
    setHasError(false);

    try {
      const response: TPaginatedUsers = await props.model.index({
        page,
        q: searchQuery.value || undefined,
        ...props.payload,
      });

      setUsers(response.data);
      setPagination(response.meta);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const findUserIndex = (id: number) =>
    users.value.findIndex((existingUser: TUser) => existingUser.id === id);

  const onEdit = (user: TUser) => {
    showModal(UpdateModal, {
      model: props.model,
      patchDataClient: props.patchDataClient,
      user,
      fullControl: props.fullControl,
      updatedCallback: (updatedUser: TUser) => {
        const index = findUserIndex(updatedUser.id);

        if (index !== -1) {
          users.value[index] = updatedUser;
        }
      },
    });
  };

  const onAdd = () => fetchUsers(PaginationCalculator.getPageOnAdd(pagination.value));

  const onDelete = (id: number) => {
    props.model
      .destroy(id)
      .then(() => fetchUsers(PaginationCalculator.getPageOnDelete(pagination.value)))
      .then(notifySuccess)
      .catch((e) => {
        // TODO: repeated
        if (e?.response?.data.message) {
          notifyFailure(e?.response?.data.message);
        }
      });
  };

  const patchesString = (patches: TUserPatch[]): string =>
    PatchLabelResolver.getListLabel(patches as TPatch[]);

  const canManageUser = (user: TUser): boolean =>
    props.fullControl || user.role === EUserRole.USER || props.authUser?.role === EUserRole.OWNER;

  const canDeleteUser = (user: TUser): boolean =>
    props.fullControl || (user.role !== EUserRole.OWNER && canManageUser(user));

  watch(searchQuery, () => fetchUsers(), { immediate: true });

  watch(
    () => props.refetchTrigger,
    () => fetchUsers(pagination.value?.current_page)
  );

  watch(
    () => props.payload,
    () => fetchUsers(1)
  );
</script>
